/*
 * orquis-gallery.js — PDP gallery: full-bleed track carousel (2026-08-06)
 *
 * Enhancement layer over Dawn's <media-gallery>. The main <ul> keeps its
 * structure and its <li> children, so product-info.js goes on reconciling
 * per-shade media and the variant featured image on shade change.
 *
 * This script:
 *   1. builds the faint thumbnail row on the foot of the panel,
 *   2. on hover/tap slides one step: the outgoing photo exits past the LEFT
 *      screen edge while the incoming enters from the RIGHT screen edge,
 *      passing under the description card (which goes translucent for the
 *      move). A full empty gap passes through the panel between them.
 *      Any jump is ONE step, so 1 -> 10 never scrolls through the rest.
 *   3. rebuilds the thumbnails when the media set changes (variant switch).
 *
 * Geometry lives here because the list is full-bleed (spans the screen) while
 * the panel is only the left half of the grid, which CSS alone cannot express.
 */
(function () {
  'use strict';

  var STAGE = '.product--orquis-stage';
  var DUR = 1800; // approved glide, keep in step with the transition set below
  var EASE = 'cubic-bezier(.4, 0, .2, 1)';

  function cssEscape(value) {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function init(gallery) {
    var list = gallery.querySelector('.product__media-list');
    var viewer = gallery.querySelector('[id^="GalleryViewer"]') || (list && list.parentElement);
    if (!list || !viewer) return;

    var product = gallery.closest('.product');
    var wrapper = gallery.closest('.product__media-wrapper') || viewer;
    var isDesktop = window.matchMedia('(min-width: 990px)');
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var panelL = 0, panelW = 0, STEP = 0, sliding = false;

    var bar = document.createElement('div');
    bar.className = 'orquis-stage__thumbs';
    bar.setAttribute('aria-label', 'Product image thumbnails');
    viewer.appendChild(bar);

    // Mobile progress bar: one segment per photo, on the top edge of the card.
    var progress = document.createElement('div');
    progress.className = 'orquis-stage__progress';
    progress.setAttribute('aria-hidden', 'true');
    var infoContainer = product && product.querySelector('.product__info-container');
    if (infoContainer) infoContainer.insertBefore(progress, infoContainer.firstChild);

    function items() {
      return Array.prototype.slice.call(list.querySelectorAll('.product__media-item'));
    }
    function activeItem() {
      return list.querySelector('.product__media-item.is-active') || items()[0] || null;
    }
    function cardEl() {
      return product && product.querySelector('.product__info-wrapper');
    }

    // Full-bleed the list (screen edge to screen edge) and rest every photo in
    // the panel. Item coordinates are screen coordinates: the list starts at 0.
    function measure() {
      if (!isDesktop.matches) {
        list.style.left = ''; list.style.width = '';
        items().forEach(function (li) {
          li.style.left = ''; li.style.width = ''; li.style.transform = ''; li.style.transition = '';
        });
        return;
      }
      var r = wrapper.getBoundingClientRect();
      var vw = document.documentElement.clientWidth;
      panelL = r.left;
      panelW = r.width;
      STEP = vw - panelL; // one step: panel position out to the screen edge
      list.style.left = -panelL + 'px';
      list.style.width = vw + 'px';
      items().forEach(function (li) {
        li.style.width = panelW + 'px';
        li.style.left = panelL + 'px';
        li.style.transform = '';
        li.style.transition = '';
      });
    }

    function commit(id, target) {
      if (typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(id, false);
      } else {
        items().forEach(function (it) { it.classList.remove('is-active'); });
        target.classList.add('is-active');
      }
      syncActive();
    }
    function highlightThumb(id) {
      Array.prototype.forEach.call(bar.children, function (btn) {
        btn.classList.toggle('is-active', btn.dataset.target === id);
      });
    }

    function activate(id) {
      if (sliding) return;
      var all = items();
      var current = activeItem();
      var target = list.querySelector('[data-media-id="' + cssEscape(id) + '"]');
      if (!target || target === current) return;

      highlightThumb(id);

      if (!isDesktop.matches) {
        // the carousel is a real scroller here: move it, and the observer above
        // flips the active photo and the progress bar.
        commit(id, target);
        if (target.scrollIntoView) target.scrollIntoView({ inline: 'center', block: 'nearest' });
        return;
      }
      if (reduce.matches || !current) {
        commit(id, target);
        return;
      }

      sliding = true;
      measure();

      // one step, whichever photo was picked: the target is placed a single
      // step away and both photos travel together.
      var sign = all.indexOf(target) > all.indexOf(current) ? 1 : -1;
      var card = cardEl();
      target.style.left = panelL + sign * STEP + 'px';
      target.classList.add('orquis-in');
      current.classList.add('orquis-out');
      void target.offsetWidth; // commit the start position before animating

      var move = 'translateX(' + -sign * STEP + 'px)';
      var tr = 'transform ' + DUR + 'ms ' + EASE;
      [current, target].forEach(function (el) {
        el.style.transition = tr;
        el.style.transform = move;
      });
      if (card) card.classList.add('orquis-card-sheer');

      window.setTimeout(function () {
        // land: drop the animation, rest both photos back in the panel, and
        // hand the active flag to the new one.
        [current, target].forEach(function (el) {
          el.style.transition = 'none';
          el.style.transform = '';
          el.style.left = panelL + 'px';
        });
        void list.offsetWidth;
        [current, target].forEach(function (el) { el.style.transition = ''; });
        target.classList.remove('orquis-in');
        current.classList.remove('orquis-out');
        commit(id, target);
        if (card) card.classList.remove('orquis-card-sheer');
        sliding = false;
      }, DUR);
    }

    function buildThumbs() {
      bar.textContent = '';
      items().forEach(function (item) {
        var id = item.dataset.mediaId;
        if (!id) return;
        var img = item.querySelector('img');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'orquis-stage__thumb';
        btn.dataset.target = id;
        btn.setAttribute('aria-label', (img && img.getAttribute('alt')) || 'Show image');
        if (img) {
          var thumb = document.createElement('img');
          thumb.src = img.currentSrc || img.src;
          thumb.alt = '';
          thumb.loading = 'lazy';
          btn.appendChild(thumb);
        }
        btn.addEventListener('mouseenter', function () { activate(id); });
        btn.addEventListener('click', function () { activate(id); });
        bar.appendChild(btn);
      });
      bar.hidden = bar.children.length < 2; // one photo needs no thumbnails

      // one progress segment per photo, same hidden rule
      progress.textContent = '';
      items().forEach(function () { progress.appendChild(document.createElement('span')); });
      progress.hidden = progress.children.length < 2;

      measure();
      syncActive();
      observeInView();
    }

    function syncActive() {
      var current = activeItem();
      var id = current ? current.dataset.mediaId : null;
      Array.prototype.forEach.call(bar.children, function (btn) {
        var on = btn.dataset.target === id;
        btn.classList.toggle('is-active', on);
        if (on) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });
      var index = items().indexOf(current);
      Array.prototype.forEach.call(progress.children, function (seg, i) {
        seg.classList.toggle('is-active', i === index);
      });
    }

    // Mobile: the swipe drives the active photo. Watching the items inside the
    // scroller keeps this off the scroll event entirely.
    var inView;
    function observeInView() {
      if (inView) inView.disconnect();
      if (isDesktop.matches || !('IntersectionObserver' in window)) return;
      inView = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var el = e.target;
          if (el.classList.contains('is-active')) return;
          items().forEach(function (it) { it.classList.remove('is-active'); });
          el.classList.add('is-active');
          syncActive();
        });
      }, { root: list, threshold: 0.6 });
      items().forEach(function (li) { inView.observe(li); });
    }

    buildThumbs();
    window.addEventListener('resize', function () {
      if (sliding) return;
      measure();
      observeInView(); // re-arm when crossing the 990px boundary
    });
    window.addEventListener('load', measure);

    // product-info.js reconciles the <ul> and toggles is-active on variant
    // change. Rebuild on a structural change, otherwise just resync.
    var pending, structural = false;
    new MutationObserver(function (mutations) {
      if (sliding) return; // our own inline styles must not retrigger this
      structural = structural || mutations.some(function (m) {
        return m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length);
      });
      window.clearTimeout(pending);
      pending = window.setTimeout(function () {
        if (structural) buildThumbs(); else { measure(); syncActive(); }
        structural = false;
      }, 16);
    }).observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  function boot() {
    document.querySelectorAll(STAGE + ' media-gallery').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
